import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Paper from '@mui/material/Paper';
import { DataGrid, DataGridColumn, TaskStatusChip, Spinner } from '@repo/components';
import { Task, TasksApiResponse } from '@repo/shared/domain';
import { sortToString, stringToSort } from '@repo/shared/helpers';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import useUpdateQueryParams from 'src/hooks/useUpdateQueryParams';
import { getCompanyApprovals } from 'src/api/company';
import { constructQuery } from '@repo/shared/helpers';
import { useBreadcrumbLevel } from '@repo/components/hooks';

type SearchParams = {
	page?: number;
	sort?: `${string}:${string}`;
};
export const Route = createFileRoute('/company_/$groupId/approvals/')({
	component: RouteComponent,
	validateSearch: (search?: SearchParams) => ({
		page: (search?.page as number) || 0,
		sort: (search?.sort as `${string}:${string}`) || 'startTime:desc',
	}),
});

const columns: DataGridColumn<Task>[] = [
	{ headerName: 'Description', field: 'name', flex: 1 },
	{
		field: 'type',
		headerName: 'Type',
		width: 100,
		renderCell: (c: { value: string }) => c.value && `${c.value?.[0]?.toUpperCase()}${c.value?.slice(1).toLowerCase()}`,
	},
	{
		field: 'createdAt',
		headerName: 'Assigned',
		width: 120,
		valueFormatter: (params: { value: string }) => new Date(params.value).toLocaleDateString('en-GB'),
	},
	{
		field: 'updatedAt',
		headerName: 'Last updated',
		width: 120,
		valueFormatter: (params: { value: string }) => new Date(params.value).toLocaleDateString('en-GB'),
	},
	{
		field: 'validTo',
		headerName: 'Valid to',
		width: 100,
		renderCell: (c) => {
			const value = c.row.validToOverride ? c.row.validToOverride : c.row.validTo;
			return value ? new Date(value).toLocaleDateString('en-GB') : null;
		},
	},
	{
		field: 'status',
		headerName: 'Status',
		renderCell: (cell) => <TaskStatusChip status={cell.row.status} />,
		sortable: false,
		width: 175,
	},
];

function RouteComponent() {
	const { groupId } = Route.useParams();
	const { page, sort } = Route.useSearch();
	const navigate = useNavigate();
	const updateQueryParams = useUpdateQueryParams();
	const { user: authenticatedUser, loading: userLoading } = useAuthenticatedUser();
	const queryClient = useQueryClient();

	const query = constructQuery({ page, sort });

	const { data: approvals, isLoading } = useQuery({
		queryKey: ['company-approvals-list', groupId, { page, sort }],
		queryFn: () => getCompanyApprovals(groupId, query),
		enabled: !userLoading,
	});

	useBreadcrumbLevel(1, 'Approvals');

	if (userLoading) {
		return null;
	}

	const handleReload = () => {
		queryClient.invalidateQueries({ queryKey: ['company-approvals-list', groupId] });
		queryClient.invalidateQueries({ queryKey: ['company-approvals', groupId] });
	};

	const handlePaginationModelChange = ({ page }: { page: number }) => {
		updateQueryParams({ page: page });
	};

	const handleSort = ([sortModel]: any[]) => {
		if (!sortModel) return;
		updateQueryParams({ sort: sortToString(sortModel) });
	};

	const handleRowClick = ({ row }: { row: Task }) => {
		navigate({
			to: '/company/$groupId/approvals/$taskId',
			params: { groupId, taskId: row.id },
		});
	};

	return (
		<Paper variant="outlined" sx={{ marginTop: 2 }}>
			<DataGrid
				data={approvals?.data || []}
				columns={columns}
				rowCount={approvals?.count || 0}
				isLoading={isLoading}
				paginationMode="server"
				sortingMode="server"
				onPaginationModelChange={handlePaginationModelChange}
				onSortModelChange={handleSort}
				sortingOrder={['asc', 'desc']}
				sortModel={[stringToSort(sort)]}
				pageSize={approvals?.pageSize}
				pageSizeOptions={[approvals?.pageSize || 0]}
				page={approvals?.page}
				onRowClick={handleRowClick}
				pagination
				onReload={handleReload}
			/>
		</Paper>
	);
}
